import NcfdStatusPanel from '../components/dashboard/NcfdStatusPanel'
import ExposureBudgetPanel from '../components/dashboard/ExposureBudgetPanel'
import { Link } from 'react-router-dom'
import { useDashboardActive } from '../components/dashboard/DashboardActivityContext.js'
import { useAccounts } from '../hooks/useAccounts'
import { usePositions } from '../hooks/usePositions'
import { useSettings } from '../hooks/useSettings'
import { fmt, calcMaxLoss } from '../lib/calculations'
import PrivateValue from '../components/PrivateValue'
import MarketRegimePanel from '../components/dashboard/MarketRegimePanel'

function StatCard({ label, value, sub, accent }) {
  return (
    <div className="min-w-0 px-3 py-2">
      <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-1">{label}</div>
      <div className={`font-mono text-xl font-semibold ${accent || 'text-ink'}`}>{value}</div>
      {sub && <div className="font-mono text-[10px] text-inkMuted mt-0.5">{sub}</div>}
    </div>
  )
}

function Portfolio() {
  const { accounts, totalEquity } = useAccounts()
  const { openPositions } = usePositions()
  const { settings } = useSettings()

  const totalMaxLoss = accounts.reduce((sum, a) =>
    sum + calcMaxLoss(a.equity || 0, settings.default_alloc_pct, settings.default_risk_pct), 0)

  const heatPct = totalEquity > 0
    ? (openPositions.length * (settings.default_alloc_pct * settings.default_risk_pct / 100)).toFixed(1)
    : '0.0'

  return (
        <section className="flex h-[440px] min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-border bg-canvas lg:h-full" aria-label="Portfolio and open positions" data-testid="portfolio-quadrant">
          <header className="flex h-9 shrink-0 items-center border-b border-border px-3">
            <h2 className="text-[11px] font-bold uppercase tracking-wide text-ink">Portfolio</h2>
          </header>
          <div className="grid shrink-0 grid-cols-2 divide-border border-border xl:grid-cols-4">
            <StatCard
              label="Total Equity"
              value={<PrivateValue>{fmt(totalEquity) || '—'}</PrivateValue>}
              sub={`${accounts.filter(a => a.equity > 0).length} accounts`}
            />
            <StatCard
              label="Max $ Risk"
              value={<PrivateValue>{fmt(totalMaxLoss) || '—'}</PrivateValue>}
              sub={`${(settings.default_risk_pct * settings.default_alloc_pct / 100).toFixed(2)}% of equity`}
              accent="text-ember"
            />
            <StatCard
              label="Open Positions"
              value={openPositions.length}
              sub="active trades"
            />
            <StatCard
              label="Portfolio Heat"
              value={<PrivateValue>{heatPct + '%'}</PrivateValue>}
              sub="estimated risk deployed"
              accent={parseFloat(heatPct) > 20 ? 'text-ember' : 'text-forest'}
            />
          </div>
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="flex h-9 shrink-0 items-center justify-between border-y border-border px-3">
              <h3 className="text-[11px] font-semibold uppercase tracking-wide text-ink">Open Positions</h3>
              <Link to="/positions" className="font-mono text-xs text-inkMuted hover:text-ink underline">View all →</Link>
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
            {openPositions.length === 0 ? (
              <div className="flex h-full min-h-24 flex-col items-center justify-center p-3 text-center">
                <div className="font-mono text-xs text-inkMuted">No open positions</div>
                <Link to="/positions" className="font-mono text-xs text-ink underline mt-2 block">Add your first position →</Link>
              </div>
            ) : (
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    {['Ticker', 'Avg Cost', 'Stop', 'Dist %', 'Tranches', 'Status'].map(h => (
                      <th key={h} className="px-4 py-3 text-left font-mono text-[10px] tracking-widest uppercase text-inkMuted">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {openPositions.slice(0, 5).map(pos => (
                    <tr key={pos.id} className="border-b border-border last:border-0 hover:bg-canvas2 transition-colors">
                      <td className="px-4 py-3 font-black text-sm tracking-wide">{pos.ticker}</td>
                      <td className="px-4 py-3 font-mono text-sm"><PrivateValue>{pos.avg_cost ? fmt(pos.avg_cost) : '—'}</PrivateValue></td>
                      <td className="px-4 py-3 font-mono text-sm text-ember"><PrivateValue>{pos.stop_price ? fmt(pos.stop_price) : '—'}</PrivateValue></td>
                      <td className="px-4 py-3 font-mono text-sm">
                        {pos.avg_cost && pos.stop_price
                          ? ((pos.avg_cost - pos.stop_price) / pos.avg_cost * 100).toFixed(1) + '%'
                          : '—'}
                      </td>
                      <td className="px-4 py-3 font-mono text-sm">
                        {pos.tranches ? JSON.parse(pos.tranches || '[]').filter(t => t.price > 0).length : '—'}/{pos.num_tranches || '—'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-mono text-[10px] px-2 py-1 rounded-full bg-canvas2 text-inkMuted border border-border">Open</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            </div>
          </div>
        </section>
  )
}

export default function Dashboard() {
  const active = useDashboardActive()
  return (
    <div className="flex min-h-0 flex-1 flex-col" data-testid="dashboard-page">
      <h1 className="sr-only">Dashboard</h1>
      <div className="grid min-h-0 flex-1 grid-cols-1 gap-1 lg:grid-cols-2 lg:grid-rows-2" data-testid="dashboard-quadrants">
        {active && <Portfolio />}
        <MarketRegimePanel />
        <NcfdStatusPanel />
        <ExposureBudgetPanel />
      </div>
    </div>
  )
}
